import { PatientReferenceIdentifier } from '@dreryk/egabinet-patient-published-language-api';

import { assertUnreachable } from '@dreryk/react-common';

import { MergeAll } from '~libs.typescript';

import {
   PatientEntitlementToMedicalServiceStatusVerificationFailureType,
   PatientEntitlementToMedicalServiceStatusVerificationInternalState,
   PatientEntitlementToMedicalServiceStatusVerificationResultType,
} from './PatientEntitlementToMedicalServiceStatusVerification.types';
import { PatientEntitlementToMedicalServiceStatus } from './patientEntitlementToMedicalServiceStatusGlobalState.types';

export const getCurrentPatientEntitlementToMedicalServicesStatus = (
   state: PatientEntitlementToMedicalServiceStatusVerificationInternalState
):
   | PatientEntitlementToMedicalServiceStatusVerificationResultType
   | PatientEntitlementToMedicalServiceStatusVerificationFailureType => {
   return 'resultType' in state ? state.resultType : state.failureType;
};

const getInsuredEwusState = (): PatientEntitlementToMedicalServiceStatusVerificationInternalState => {
   return {
      resultType: 'INSURED',
      retryAvailable: false,
   };
};

const getEntitlementStatementState = (): PatientEntitlementToMedicalServiceStatusVerificationInternalState => {
   return {
      resultType: 'VALID_ENTITLEMENT_STATEMENT',
      retryAvailable: false,
   };
};

const getUninsuredEwusState = (): PatientEntitlementToMedicalServiceStatusVerificationInternalState => {
   return {
      resultType: 'UNINSURED',
      retryAvailable: true,
   };
};

type UnverifiedEwusStateData = {
   checkDate: Date;
   patientReferenceIdentifier?: PatientReferenceIdentifier;
};

const getUnverifiedEwusState = (
   data: UnverifiedEwusStateData
): PatientEntitlementToMedicalServiceStatusVerificationInternalState => {
   if (!data.patientReferenceIdentifier?.findExternalIdentifier()) {
      return {
         resultType: 'UNVERIFIED_WITHOUT_EXTERNAL_IDENTIFIER',
         retryAvailable: false,
      };
   }

   const currentDate = new Date();
   const dateOfCheck = new Date(data.checkDate);
   dateOfCheck.setHours(0, 0, 0, 0);

   if (currentDate < dateOfCheck) {
      return {
         resultType: 'UNVERIFIED_IN_FUTURE',
         retryAvailable: false,
      };
   }

   return {
      resultType: 'UNVERIFIED',
      retryAvailable: true,
   };
};

type VerificationFailureEwusStateData = {
   failureType: PatientEntitlementToMedicalServiceStatusVerificationFailureType;
};

export const getVerificationFailureEwusState = (
   data: VerificationFailureEwusStateData
): PatientEntitlementToMedicalServiceStatusVerificationInternalState => {
   return {
      failureType: data.failureType,
      retryAvailable: true,
   };
};

export type PatientEntitlementToMedicalServiceStatusVerificationData = MergeAll<
   [
      PatientEntitlementToMedicalServiceStatus,
      {
         checkDate: Date;
         patientReferenceIdentifier?: PatientReferenceIdentifier;
      }
   ]
>;

export const getPatientEntitlementToMedicalServiceStatusVerificationState = (
   data: PatientEntitlementToMedicalServiceStatusVerificationData
): PatientEntitlementToMedicalServiceStatusVerificationInternalState => {
   if (data.entitlementStatementStatus === 'VALID_ENTITLEMENT_STATEMENT') {
      return getEntitlementStatementState();
   }

   switch (data.ewusStatus) {
      case 'INSURED':
         return getInsuredEwusState();
      case 'UNINSURED':
         return getUninsuredEwusState();
      case 'UNVERIFIED':
         return getUnverifiedEwusState({
            checkDate: data.checkDate,
            patientReferenceIdentifier: data.patientReferenceIdentifier,
         });
      case 'VERIFICATION_FAILURE':
         return getVerificationFailureEwusState({
            failureType: 'VERIFICATION_FAILURE',
         });
      default:
         return assertUnreachable(data.ewusStatus);
   }
};
